/**
 * Generate device fingerprint using SHA-256 hash
 * Uses stable identifiers - fingerprint will remain stable unless Excel is updated
 * 
 * Components used:
 * - userId: Unique user ID from Office SSO (Azure AD Object ID) - stable per user
 * - platform: OS platform (PC, Mac, iOS, etc.) - stable
 * - host: Office host (Excel, Word, etc.) - stable
 * - officeVersion: Office version - changes only with Excel updates (acceptable)
 * 
 * NOT using userAgent - it changes with browser/WebView updates (too frequent)
 */

/**
 * User information from Office SSO
 */
interface UserInfoResult {
    userId: string | null;
    email: string | null;
    name: string | null;
}

/**
 * Get complete user information from Office SSO
 * Returns user ID (oid), email, and name from Azure AD token
 * 
 * NOTE: This only works for:
 * - Microsoft 365 subscriptions (logged in users)
 * - Office Online/Web
 * 
 * Does NOT work for:
 * - Excel with volume/standalone license (activated with product key)
 * - Users not signed into Microsoft 365
 * 
 * In such cases, returns null values (which is expected and handled)
 */
const getUserInfo = async (): Promise<UserInfoResult> => {
    const defaultResult: UserInfoResult = {
        userId: null,
        email: null,
        name: null
    };

    try {
        // Check if SSO is available (Office.auth exists)
        if (!Office.auth || typeof Office.auth.getAccessToken !== 'function') {
            console.log('ℹ️ Office SSO not available (standalone/volume license or unsupported Office version)');
            return defaultResult;
        }

        // Get access token using Office SSO
        // This will fail silently for standalone Excel licenses
        const token = await Office.auth.getAccessToken({ 
            allowSignInPrompt: false,  // Don't force login
            allowConsentPrompt: false, // Don't force consent
            forMSGraphAccess: false
        });
        
        // Decode JWT token (base64)
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        
        const payload = JSON.parse(jsonPayload);
        
        // Extract user information from token payload
        // oid = unique Object ID from Azure AD (stable, unique per user)
        const userId = payload.oid || payload.sub || null;
        const email = payload.preferred_username || payload.upn || payload.email || null;
        const name = payload.name || null;
        
        console.log('👤 User info from SSO:', { 
            userId: userId ? userId.substring(0, 8) + '...' : null,
            email,
            name
        });
        
        return {
            userId,
            email,
            name
        };
    } catch (error: any) {
        // Expected errors for standalone/volume licenses:
        // - 13001: User not signed in
        // - 13002: User interaction required
        // - 13003: Not supported
        // - 13012: Platform doesn't support
        
        const errorCode = error?.code;
        if (errorCode === 13001) {
            console.log('ℹ️ User not signed into Microsoft 365 (standalone/volume license)');
        } else if (errorCode === 13003 || errorCode === 13012) {
            console.log('ℹ️ SSO not supported on this platform');
        } else {
            console.warn('⚠️ Could not get user info from SSO:', error?.message || error);
        }
        
        return defaultResult;
    }
};

/**
 * Get logged-in user identifier from Office SSO (backward compatibility)
 * Returns unique user ID (oid) from Azure AD token
 * @deprecated Use getUserInfo() instead to get complete user information
 */
const getUserId = async (): Promise<string | null> => {
    const userInfo = await getUserInfo();
    return userInfo.userId;
};

/**
 * Get device-specific identifier for standalone Excel installations
 * Uses localStorage to generate and persist a unique device ID
 * This is stable across sessions on the same device/browser
 */
const getDeviceId = (): string => {
    const STORAGE_KEY = 'carbowaste_device_id';
    
    try {
        // Try to get existing device ID from localStorage
        const storedId = localStorage.getItem(STORAGE_KEY);
        if (storedId) {
            return storedId;
        }
        
        // Generate new unique device ID using crypto API
        // Format: dev_{timestamp}_{random}
        const timestamp = Date.now().toString(36);
        const randomBytes = new Uint8Array(16);
        crypto.getRandomValues(randomBytes);
        const randomPart = Array.from(randomBytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('')
            .substring(0, 16);
        
        const newId = `dev_${timestamp}_${randomPart}`;
        localStorage.setItem(STORAGE_KEY, newId);
        
        console.log('🆔 Generated new device ID for standalone Excel:', newId.substring(0, 20) + '...');
        return newId;
    } catch (error) {
        console.warn('⚠️ Could not use localStorage for device ID, using fallback');
        // Fallback: use timestamp-based ID (will change each session)
        return `dev_fallback_${Date.now().toString(36)}`;
    }
};

/**
 * Get operating system information
 * Returns OS name and version for desktop platforms, or 'Web' for browser
 */
const getOsInfo = (): string => {
    try {
        const platform = Office.context.platform;
        const userAgent = navigator.userAgent;
        
        // For Web (OfficeOnline)
        if (platform === Office.PlatformType.OfficeOnline) {
            return 'Web';
        }
        
        // For PC (Windows)
        if (platform === Office.PlatformType.PC) {
            // Extract Windows version from userAgent
            // Examples: "Windows NT 10.0" = Windows 10/11, "Windows NT 6.3" = Windows 8.1
            const windowsMatch = userAgent.match(/Windows NT (\d+\.\d+)/);
            if (windowsMatch) {
                const ntVersion = windowsMatch[1];
                // Map NT versions to Windows versions
                const versionMap: Record<string, string> = {
                    '10.0': 'Win10/11', // Windows 10 and 11 both report 10.0
                    '6.3': 'Win8.1',
                    '6.2': 'Win8',
                    '6.1': 'Win7',
                };
                return versionMap[ntVersion] || `WinNT${ntVersion}`;
            }
            return 'Windows';
        }
        
        // For Mac
        if (platform === Office.PlatformType.Mac) {
            // Extract macOS version from userAgent
            // Example: "Mac OS X 10_15_7" or "Macintosh; Intel Mac OS X 14_1"
            const macMatch = userAgent.match(/Mac OS X (\d+)[_.](\d+)/);
            if (macMatch) {
                const majorVersion = macMatch[1];
                const minorVersion = macMatch[2];
                return `macOS${majorVersion}.${minorVersion}`;
            }
            return 'macOS';
        }
        
        // For iOS/iPadOS
        if (platform === Office.PlatformType.iOS) {
            // Extract iOS version from userAgent
            // Example: "CPU iPhone OS 16_5 like Mac OS X" or "CPU OS 16_5 like Mac OS X"
            const iosMatch = userAgent.match(/(?:iPhone )?OS (\d+)[_.](\d+)/);
            if (iosMatch) {
                const majorVersion = iosMatch[1];
                const minorVersion = iosMatch[2];
                return `iOS${majorVersion}.${minorVersion}`;
            }
            return 'iOS';
        }
        
        // For Android
        if (platform === Office.PlatformType.Android) {
            const androidMatch = userAgent.match(/Android (\d+(?:\.\d+)?)/);
            if (androidMatch) {
                return `Android${androidMatch[1]}`;
            }
            return 'Android';
        }
        
        // Universal (catch-all)
        if (platform === Office.PlatformType.Universal) {
            return 'Universal';
        }
        
        // Fallback
        return 'Unknown';
    } catch (error) {
        console.warn('⚠️ Could not determine OS info:', error);
        return 'Unknown';
    }
};

/**
 * Generate device fingerprint using SHA-256 hash
 * Uses stable identifiers - fingerprint will remain stable unless Excel is updated
 * 
 * Components used:
 * - userId: Unique user ID from Office SSO (Azure AD Object ID) - stable per user
 *   (only for Microsoft 365 subscriptions; null for standalone licenses)
 * - deviceId: Unique device ID from localStorage (for standalone licenses)
 * - osInfo: Operating system name and version (e.g., "Win10/11", "macOS14.1", "iOS16.5", "Web")
 * - host: Office host (Excel, Word, etc.) - stable
 * - officeVersion: Office version - changes only with Excel updates (acceptable)
 * 
 * For standalone Excel licenses (without M365):
 * - userId will be null → uses deviceId from localStorage
 * - deviceId is generated once and persisted in browser storage
 * - Stable across sessions on same device/browser
 * - Different for each browser (Chrome vs Edge vs Safari)
 * 
 * OS version is extracted from userAgent but is relatively stable (changes only with OS updates)
 */
const getMachineFingerprint = async (): Promise<string> => {
    try {
        // Gather Office information - stable across sessions
        const officeVersion = Office.context.diagnostics.version || 'unknown';
        const osInfo = getOsInfo();
        const host = Office.context.host || 'unknown';
        
        // Get user info from SSO (if available)
        const userInfo = await getUserInfo();
        
        // For standalone Excel (no userId), use device-specific identifier
        const userComponent = userInfo.userId || getDeviceId();

        // Create fingerprint string combining user/device + OS + Office properties
        // This creates unique fingerprint per user OR per device (for standalone)
        const info = `${userComponent}-${osInfo}-${host}-${officeVersion}`;

        // Generate SHA-256 hash
        const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(info));
        const fingerprint = Array.from(new Uint8Array(buffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
        
        console.log('🔑 Generated fingerprint from:', { 
            userId: userInfo.userId ? userInfo.userId.substring(0, 8) + '...' : null,
            deviceId: !userInfo.userId ? userComponent.substring(0, 20) + '...' : null,
            osInfo,
            host, 
            officeVersion 
        });
        
        return fingerprint;
    } catch (error) {
        console.error('❌ Error creating machine fingerprint:', error);
        
        // Fallback to basic fingerprint without version
        const basicInfo = `${Office.context.platform}-${Office.context.host}`;
        const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(basicInfo));
        return Array.from(new Uint8Array(buffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
    }
};

export type { UserInfoResult };

export { 
    getMachineFingerprint,
    getUserInfo,
    getUserId,
    getDeviceId,
    getOsInfo,
};

